import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import type { SessionMessageResponse } from "@/lib/session";

/**
 * Convert a persisted session-message API row into the message shape used by
 * the chat UI. Keeping this mapping shared ensures the main conversation,
 * child-session panel, and recovery polling preserve the same render data.
 */
export function mapSessionMessage(
  message: SessionMessageResponse,
): AgnoChatMessage {
  const metadata = message.metadata;

  return {
    id: message.id,
    role: message.role as AgnoChatMessage["role"],
    content: message.content,
    parts: [{ type: "text", text: message.content }],
    metadata: {
      createdAt: message.created_at,
      ...(message.scheduled_run ? { scheduledRun: message.scheduled_run } : {}),
      runId: message.run_id,
      status: message.status,
      phase: metadata?.phase,
      toolName: metadata?.tool_name ?? metadata?.toolName,
      workflowTaskId: metadata?.workflow_task_id,
      sessionId: message.session_id,
      navigationTurnId: message.navigation_turn_id ?? undefined,
      navigationPartialContent: metadata?.navigation_partial_content === true,
      navigationContentCursor: metadata?.navigation_content_cursor,
      navigationContentRevision: metadata?.navigation_content_revision,
      tokens_before: metadata?.tokens_before,
      summary_length: metadata?.summary_length,
      flashMode: metadata?.flash_mode === true,
      // Only the display-safe composer action fields survive history mapping.
      ...(metadata?.source === "composer_action"
        && typeof metadata.action_label === "string"
        && typeof metadata.task_id === "string"
        ? {
            source: "composer_action",
            action_label: metadata.action_label,
            task_id: metadata.task_id,
          }
        : {}),
      // HITL pause marker fields — persisted rows reconstruct the approval
      // card after a page reload instead of relying on ephemeral SSE state.
      hitlPaused: metadata?.phase === "hitl_paused",
      requirements: metadata?.requirements,
      hitlResolved: metadata?.resolved ?? false,
      hitlRecoveryError: metadata?.recoverable_error,
      ...(metadata?.voiceTranscript !== undefined
        ? { voiceTranscript: metadata.voiceTranscript }
        : {}),
    },
  };
}
